import classNames from "classnames";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Message } from "semantic-ui-react";
import { useElementHeightWithResize, useOnClickOutside } from "utilities/hookutils";
import { InPageEditingContext } from "./InPageEditingContext";
import { InPageEditableFieldProps, variantClassNameDefinitions } from "./InPageEditableField";

export const InPageHoverableField = ({
  compact,
  field,
  variant = "default",
  darkerHover,
}: InPageEditableFieldProps) => {
  const variantClassNames = variantClassNameDefinitions[variant];

  const [isHovering, setIsHovering] = useState(false);

  // Locked-in state allows user to move mouse outside the field while keeping the edit view open
  // It gets enabled on click while hovering, and gets reset when edit view is closed (either by saving, cancelling, or clicking outside).
  const [isLockedIn, setIsLockedIn] = useState(false);
  const supportsHover = useMemo(() => window.matchMedia?.("(hover: hover)")?.matches ?? true, []);
  const { ref: hoverReadonlyRef, height: hoverReadonlyHeight } = useElementHeightWithResize<HTMLDivElement>();

  const {
    enabled,
    editingField,
    startInPageEdit,
    updateValue,
    saveInPageEdit,
    clearInPageEdit,
  } = useContext(InPageEditingContext);

  const inPageEditRef = useRef<HTMLDivElement>(null);
  const hoverWrapperRef = useRef<HTMLDivElement>(null);
  const disableClickOutside = field.inPageEdit?.disableClickOutside ?? false;

  const isInPageEditable = enabled && field.inPageEdit !== undefined && field.editDisabled !== true;
  const isEdited = editingField.fieldKey === field.key;

  useOnClickOutside(hoverWrapperRef, (e) => onClickOutside(e));

  // Reset locked-in state when editing ends
  useEffect(() => {
    if (!isEdited && isLockedIn) {
      setIsLockedIn(false);
    }
  }, [isEdited, isLockedIn]);

  // Detect mouse moving to elements outside the wrapper (only when not locked in)
  // This is necessary for modals. Going from hover to an open modal won't trigger onMouseLeave
  useEffect(() => {
    if (!isHovering) return;

    const handleMouseOver = (e: MouseEvent) => {
      if (hoverWrapperRef.current && !hoverWrapperRef.current.contains(e.target as Node)) {
        setIsHovering(false);
      }
    };

    document.addEventListener("mouseover", handleMouseOver);
    return () => document.removeEventListener("mouseover", handleMouseOver);
  }, [isHovering, isLockedIn]);

  const onHoverEnter = () => {
    setIsHovering(true);
  };

  const onHoverLeave = () => {
    setIsHovering(false);
  };

  const onMobileClickToEdit = (e: React.MouseEvent<HTMLElement>) => {
    if (isEdited) return;
    const clickedOnAnchor = (e.target as HTMLElement)?.closest?.("a") ?? false;
    const clickedOnButton = (e.target as HTMLElement)?.closest?.("button") ?? false;
    if (clickedOnAnchor || clickedOnButton) return;
    if (editingField.fieldKey === undefined && !editingField.isUpdating) {
      startInPageEdit(field);
    }
  };

  const onClickOutside = (e: React.MouseEvent<HTMLElement>) => {
    if (!isEdited) return;

    const clickedInsidePopper = (e.target as HTMLElement)?.closest?.(".popup") ?? false;
    const clickedInsideModal = (e.target as HTMLElement)?.closest?.(".modals") ?? false;
    if (disableClickOutside || clickedInsidePopper || clickedInsideModal) {
      return;
    }

    if (editingField.isUpdating) {
      return;
    }

    // Hover-edit: click outside cancels (does not save)
    clearInPageEdit();
    setIsHovering(false);
    setIsLockedIn(false);
  };

  const onChange = (value: any, submit: boolean) => {
    if (submit) {
      saveInPageEdit(field, value);
    } else {
      updateValue(field, value);
    }
  };

  if (!isInPageEditable) {
    return <div className={classNames(field.valueClassName, {
      "main-section-template-fields-padding": (!compact && !field.compact),
      "main-section-template-fields-empty": field.isEmpty,
      "main-section-template-fields-no-label": field.hideLabel,
      "main-section-template-fields-disabled": field.editDisabled,
    })}>
      {field.isEmpty ? <span>{RESX.Advancing.NoValue}</span> : field.render()}
    </div>
  }

  const onClickToLockIn = () => {
    if (isLockedIn) return;
    setIsLockedIn(true);
    if (editingField.fieldKey !== field.key && !editingField.isUpdating) {
      startInPageEdit(field);
    }
  };

  const hoverHandlers = supportsHover
    ? { onMouseEnter: onHoverEnter, onMouseLeave: onHoverLeave, onClick: onClickToLockIn }
    : { onClick: onMobileClickToEdit };

  const readonlyContent = field.isEmpty ? <span>{RESX.Advancing.NoValue}</span> : field.render();
  const isContextEditing = isEdited;
  const displayEditView = supportsHover ? (isHovering || isLockedIn) : isEdited;
  const isActivelyEditing = displayEditView && !editingField.isUpdating;
  const isSaving = isContextEditing && editingField.isUpdating && !field.inPageEdit?.useCustomSavingState;
  return (
    <div ref={hoverWrapperRef} {...hoverHandlers}>
      {/* Readonly view - always rendered, hidden during active editing */}
      <div
        ref={hoverReadonlyRef}
        className={classNames(field.valueClassName, variantClassNames.notEditedWrapper, {
          "main-section-template-fields-in-page-editing-loading-wrapper": isSaving,
          "main-section-template-fields-empty": field.isEmpty,
          "main-section-template-fields-no-label": field.hideLabel,
          "main-section-template-fields-error": field.error,
          "main-section-template-fields-cursor-loading": isSaving || editingField.isUpdating,
          "in-page-editing-dark": darkerHover,
        })}
        style={isActivelyEditing ? { display: "none" } : undefined}
      >
        {readonlyContent}
        {isSaving && (
          <div className={classNames("main-section-template-fields-in-page-editing-loading-animation", {
            "in-page-editing-dark": darkerHover,
          })} />
        )}
      </div>

      {/* Edit view, always in the DOM, hidden when not actively editing */}
      <div
        className={classNames(field.valueClassName, variantClassNames.editedWrapper, "main-section-template-fields-editable-value", {
          "main-section-template-fields-no-label": field.hideLabel,
          "in-page-editing-dark": darkerHover,
        })}
        style={isActivelyEditing ? { minHeight: hoverReadonlyHeight } : { display: "none" }}
      >
        <div ref={inPageEditRef} className="main-section-template-fields-in-page-editing-form-wrapper">
          <div className={classNames("main-section-template-fields-in-page-editing-form", {
            "grow1": field.inPageEdit.fullWidth || field.inPageEdit.canRemove,
          })}>
            {field.inPageEdit.render({
              value: isContextEditing ? editingField.value : field.inPageEdit.initialValue,
              onChange,
              cancel: isContextEditing ? clearInPageEdit : () => setIsHovering(false),
              submit: isContextEditing ? () => saveInPageEdit(field, editingField.value) : () => { },
              error: isContextEditing ? Boolean(editingField.error) : false,
              isSaving: isContextEditing ? editingField.isUpdating : false,
            })}
          </div>
        </div>
        {editingField.error && <Message color="red">{editingField.error}</Message>}
      </div>
    </div>
  );
};
